import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles, Anchor, Wrench, Mountain, Bike, Map, PartyPopper } from "lucide-react";
import { findVertical, VERTICALS } from "@/data/verticals";
import CTABand from "@/components/CTABand";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

const ICONS = { marine: Anchor, equipment: Wrench, recreation: Mountain, powersports: Bike, tours: Map, events: PartyPopper };

export default function IndustryDetail() {
  const { slug } = useParams();
  const v = findVertical(slug);

  useSeo({
    title: v ? `${v.name} rentals · ${v.tagline}` : "Industry",
    description: v ? `${v.sub}` : "RentAnything.ai industry page",
    path: `/industries/${slug || ""}`,
  });
  useReveal();

  if (!v) return <Navigate to="/industries" replace />;

  const Icon = ICONS[v.slug] ?? Sparkles;

  const others = VERTICALS.filter((x) => x.slug !== v.slug).slice(0, 3);

  return (
    <>
      <section className="relative overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${v.accent}`} aria-hidden="true" />
        <div className="relative ra-container py-20 sm:py-28">
          <Link to="/industries" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ra-ink/70 hover:text-ra-ocean" data-testid="industry-back-link">
            <ArrowLeft size={14} /> All industries
          </Link>
          <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-card text-ra-ocean">
                <Icon size={24} />
              </div>
              <h1 className="mt-6 ra-h1" data-testid="industry-headline">{v.headline}</h1>
              <p className="mt-5 ra-lead max-w-xl">{v.sub}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" className="ra-btn-primary" data-testid="industry-demo-btn">
                  Book a Demo <ArrowRight size={16} />
                </Link>
                <Link to="/product" className="ra-btn-ghost">See all features</Link>
              </div>
            </div>

            {/* What you can rent */}
            <div className="rounded-3xl border border-ra-line bg-white p-7 shadow-card reveal">
              <span className="ra-eyebrow">What you can rent</span>
              <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {v.examples.map((e) => (
                  <li key={e} className="flex items-center gap-2 text-sm text-ra-ink/80">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ra-ocean/10 text-ra-ocean">
                      <Check size={12} />
                    </span>
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">Built for {v.name.toLowerCase()}</span>
            <h2 className="mt-3 ra-h2">The platform configured the way your business runs.</h2>
          </div>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2">
            {v.features.map((f, i) => (
              <li key={i} className="ra-card reveal" data-testid={`industry-feature-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ra-amber/15 text-ra-amberDeep">
                    <Check size={14} />
                  </div>
                  <p className="text-sm leading-relaxed text-ra-ink/85">{f}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* USE CASE */}
      <section className="ra-section bg-ra-paper border-y border-ra-line">
        <div className="ra-container">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:items-center">
            <div className="reveal">
              <span className="ra-eyebrow">Sample use case</span>
              <h2 className="mt-3 ra-h2">{v.useCase.title}</h2>
            </div>
            <div className="reveal">
              <div className="rounded-3xl border border-ra-line bg-white p-8 shadow-card text-base leading-relaxed text-ra-ink/85">
                {v.useCase.body}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CROSS-LINK */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="flex items-end justify-between gap-4 reveal">
            <div>
              <span className="ra-eyebrow">Other verticals</span>
              <h2 className="mt-3 ra-h2">More ways operators rent with us</h2>
            </div>
            <Link to="/industries" className="hidden sm:inline-flex ra-btn-ghost">View all</Link>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => {
              const OIcon = ICONS[o.slug] ?? Sparkles;
              return (
                <Link key={o.slug} to={`/industries/${o.slug}`} className="ra-card group reveal" data-testid={`industry-cross-${o.slug}`}>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ra-ocean/10 text-ra-ocean">
                    <OIcon size={18} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-ra-ink">{o.name}</h3>
                  <p className="mt-1 text-sm text-ra-ink/65">{o.tagline}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-ra-ocean">
                    Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand
        eyebrow={`${v.name} rentals`}
        title={`Run your ${v.name.toLowerCase()} fleet like an operator who has their software figured out.`}
      />
    </>
  );
}
